import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import VictoryModal from '../../components/game/VictoryModal';
import { useGameProgress } from '../../hooks/useGameProgress';
import { useSessionContext } from '../../hooks/useSessionContext';
import SessionBanner from '../../components/game/SessionBanner';

interface ITVExpressConfig {
  total_matriculas: number;
  apariciones_objetivo: number;
  velocidad_ms: number;
  porcentaje_distractores: number;
  tiempo_medio_maximo: number;
  porcentaje_aciertos_minimo: number;
}

interface SequenceItem {
  matricula: string;
  calle: string;
  esObjetivo: boolean;
}

interface ITVExpressLevel {
  difficulty: number;
  config: ITVExpressConfig;
  sequence: SequenceItem[];
  seed: number;
}

interface Attempt {
  matricula: string;
  calle: string;
  esObjetivo: boolean;
  respuesta: string | null;
  tiempoReaccion: number | null;
  acierto: boolean;
}

export default function ITVExpressGame() {
  const { dificultad } = useParams<{ dificultad: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [level, setLevel] = useState<ITVExpressLevel | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Game state
  const [gameState, setGameState] = useState<'input' | 'playing' | 'results'>('input');
  const [matriculaJugador, setMatriculaJugador] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [attempts, setAttempts] = useState<Attempt[]>([]);
  const [waiting, setWaiting] = useState(false);
  const [startTime, setStartTime] = useState(0);
  const [gameStartTime, setGameStartTime] = useState(0);
  const [showVictory, setShowVictory] = useState(false);

  // Results
  const [results, setResults] = useState<any>(null);

  const timeoutRef = useRef<number | null>(null);

  const dificultadOrden = parseInt(dificultad || '1');
  const { saveProgress: saveGameProgress } = useGameProgress('itv-express', dificultadOrden);
  const { isInSession, sessionType, saveSessionResult } = useSessionContext();

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleMatriculaInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.toUpperCase().replace(/[^0-9A-Z]/g, '');
    if (val.length > 4) {
      val = val.slice(0, 4) + ' ' + val.slice(4, 7);
    }
    setMatriculaJugador(val);
  };

  const isValidMatricula = (mat: string) => {
    return /^[0-9]{4}\s[A-Z]{3}$/.test(mat);
  };

  const loadGame = async () => {
    if (!isValidMatricula(matriculaJugador)) {
      setError('Formato de matrícula inválido. Use: 1234 ABC');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const urlSeed = searchParams.get('seed');
      const seedToUse = urlSeed ? parseInt(urlSeed) : undefined;

      const response = await api.get('/itv-express/generate', {
        params: {
          dificultad: dificultadOrden,
          matricula: matriculaJugador,
          ...(seedToUse && { seed: seedToUse })
        }
      });

      const data: ITVExpressLevel = response.data.data;
      setLevel(data);

      // Update URL with seed
      const newUrl = `${window.location.pathname}?seed=${data.seed}`;
      window.history.replaceState({}, '', newUrl);

      // Start game after 2 seconds
      setGameState('playing');
      setGameStartTime(Date.now());
      setTimeout(() => {
        showNext(data.sequence, []);
      }, 2000);
    } catch (err: any) {
      console.error('Error al cargar juego:', err);
      setError(err.response?.data?.error || 'Error al cargar el juego');
    } finally {
      setLoading(false);
    }
  };

  const showNext = (sequence: SequenceItem[], currentAttempts: Attempt[]) => {
    const idx = currentAttempts.length;

    if (idx >= sequence.length) {
      finishGame(currentAttempts);
      return;
    }

    const item = sequence[idx];
    setCurrentIndex(idx);
    setStartTime(Date.now());
    setWaiting(true);

    // Add attempt placeholder
    const newAttempt: Attempt = {
      matricula: item.matricula,
      calle: item.calle,
      esObjetivo: item.esObjetivo,
      respuesta: null,
      tiempoReaccion: null,
      acierto: false
    };

    const updatedAttempts = [...currentAttempts, newAttempt];
    setAttempts(updatedAttempts);

    // Auto-advance after exposure time
    timeoutRef.current = setTimeout(() => {
      setWaiting(false);
      showNext(sequence, updatedAttempts);
    }, level?.config.velocidad_ms || 2000);
  };

  const handleResponse = (calle: string) => {
    if (!waiting || !level) return;

    setWaiting(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    const tiempoReaccion = Math.round(Date.now() - startTime);
    const updatedAttempts = [...attempts];
    const lastAttempt = updatedAttempts[updatedAttempts.length - 1];

    lastAttempt.respuesta = calle;
    lastAttempt.tiempoReaccion = tiempoReaccion;
    lastAttempt.acierto = lastAttempt.esObjetivo && lastAttempt.calle === calle;

    setAttempts(updatedAttempts);

    // Visual feedback
    setTimeout(() => {
      showNext(level.sequence, updatedAttempts);
    }, 400);
  };

  const finishGame = async (finalAttempts: Attempt[]) => {
    if (!level) return;

    try {
      const response = await api.post('/itv-express/validate', {
        attempts: finalAttempts,
        config: level.config
      });

      const validationResults = response.data.data;
      setResults(validationResults);
      setGameState('results');

      if (validationResults.superado) {
        const tiempoTotal = Math.floor((Date.now() - gameStartTime) / 1000);
        await saveProgress(tiempoTotal, validationResults);
        setShowVictory(true);
      }
    } catch (err: any) {
      console.error('Error validando:', err);
      setError('Error al validar resultados');
    }
  };

  const saveProgress = async (tiempo: number, validationResults: any) => {
    try {
      // Guardar progreso normal (siempre)
      await saveGameProgress({
        tablero_id: level?.seed || 0,
        tiempo,
        movimientos: validationResults.porcentajeAciertos,
        completado: true,
      });

      // Si viene de una sesión, guardar también en la sesión
      await saveSessionResult({
        tiempo,
        movimientos: validationResults.porcentajeAciertos,
        completado: true,
        datos_juego: {
          tablero_id: level?.seed || 0,
          matricula: matriculaJugador,
          porcentajeAciertos: validationResults.porcentajeAciertos,
        },
      });
    } catch (error) {
      console.error('Error guardando progreso:', error);
    }
  };

  const resetGame = () => {
    setGameState('input');
    setMatriculaJugador('');
    setLevel(null);
    setCurrentIndex(0);
    setAttempts([]);
    setWaiting(false);
    setResults(null);
    setError('');
  };

  const getCurrentItem = (): SequenceItem | null => {
    if (!level || currentIndex >= level.sequence.length) return null;
    return level.sequence[currentIndex];
  };

  const currentItem = getCurrentItem();

  // Get LED colors (random for difficulty)
  const getLEDColors = () => {
    const colors = ['led-green', 'led-red'];
    return {
      plate: colors[Math.floor(Math.random() * colors.length)],
      street: colors[Math.floor(Math.random() * colors.length)]
    };
  };

  const ledColors = getLEDColors();

  const difficulties = [
    { orden: 1, nombre: 'Neurona', icono: '🧠' },
    { orden: 2, nombre: 'Sinapsis', icono: '⚡' },
    { orden: 3, nombre: 'Cerebro', icono: '🧩' },
    { orden: 4, nombre: 'Genio', icono: '💡' },
    { orden: 5, nombre: 'Einstein', icono: '🎓' },
  ];

  return (
    <div className="h-full bg-gradient-to-br from-blue-900 to-indigo-900 flex flex-col">
      <style>{`
        .led-green { color: #00ff41; }
        .led-red { color: #ff1744; }
      `}</style>

      {isInSession && sessionType && <SessionBanner sessionType={sessionType as 'presencial' | 'remota'} />}

      {/* Compact top bar */}
      <div className="bg-black/20 backdrop-blur-sm px-3 py-2 flex items-center gap-2 flex-wrap">
        <span className="text-white font-bold text-base whitespace-nowrap">🚗 ITV Express</span>
        <div className="w-px h-5 bg-white/30 hidden sm:block" />
        <div className="flex gap-1 flex-wrap">
          {difficulties.map(d => (
            <button
              key={d.orden}
              onClick={() => { navigate(`/juegos/itv-express/${d.orden}`); resetGame(); }}
              className={`px-2 py-0.5 rounded text-xs font-semibold transition-all ${
                dificultadOrden === d.orden
                  ? 'bg-white text-blue-900'
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              {d.icono} {d.nombre}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <div className="flex gap-1 items-center">
          {/* Reset */}
          <button
            onClick={resetGame}
            title="Reiniciar"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </div>

      {error && (
        <div className="mx-3 mt-2 p-2 bg-red-500/20 border border-red-400/50 text-red-200 rounded text-sm">
          {error}
        </div>
      )}

      {/* Game Area */}
      <div className="flex-1 flex flex-col items-center justify-center p-3">

        {/* Input Screen */}
        {gameState === 'input' && (
          <div className="w-full max-w-md bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center">
            <h3 className="text-xl font-bold text-white mb-2">Introduce tu matrícula</h3>
            <p className="text-blue-200 text-sm mb-5">Formato español: 1234 ABC</p>

            <div className="inline-flex items-center bg-white border-4 border-gray-800 rounded-lg overflow-hidden shadow-xl mb-5">
              <div
                className="w-10 h-14 bg-cover bg-center flex-shrink-0"
                style={{ backgroundImage: "url('data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 60 90%22%3E%3Crect fill=%22%230033a0%22 width=%2260%22 height=%2290%22/%3E%3Ccircle fill=%22%23ffc400%22 cx=%2230%22 cy=%2220%22 r=%228%22/%3E%3Crect fill=%22white%22 y=%2235%22 width=%2260%22 height=%2220%22/%3E%3Ctext x=%2230%22 y=%2250%22 text-anchor=%22middle%22 font-size=%2214%22 fill=%22%230033a0%22 font-weight=%22bold%22%3EE%3C/text%3E%3C/svg%3E')" }}
              />
              <input
                type="text"
                value={matriculaJugador}
                onChange={handleMatriculaInput}
                placeholder="1234 ABC"
                maxLength={8}
                className="font-black text-3xl text-center px-4 py-3 border-none outline-none uppercase tracking-wider w-48"
                style={{ fontFamily: 'Arial Black, sans-serif', letterSpacing: '0.15em' }}
              />
            </div>

            <div className="mb-5">
              <button
                onClick={loadGame}
                disabled={!isValidMatricula(matriculaJugador) || loading}
                className="bg-green-600 text-white px-8 py-3 rounded-lg font-bold text-lg hover:bg-green-700 disabled:bg-gray-500 disabled:cursor-not-allowed transition-all"
              >
                {loading ? 'Cargando...' : 'Comenzar'}
              </button>
            </div>

            <div className="p-4 bg-white/10 rounded-lg text-left text-sm text-blue-100">
              <p className="font-bold mb-2">Como jugar:</p>
              <ul className="space-y-1">
                <li>• Apareceran matriculas con letras de calle (A, B, C, D)</li>
                <li>• Cuando veas TU matricula, pulsa el boton de la calle</li>
                <li>• El sistema mide tu tiempo de reaccion y precision</li>
              </ul>
            </div>
          </div>
        )}

        {/* Game Screen */}
        {gameState === 'playing' && level && (
          <div className="w-full max-w-lg">
            <div className="bg-gray-900 border-4 border-gray-700 rounded-xl p-4 shadow-2xl mb-3">
              {/* LED Display */}
              <div className="bg-black border-4 border-gray-800 rounded-lg p-4 relative shadow-inner mb-4" style={{ minHeight: '200px' }}>
                <div className="absolute top-2 left-3 text-gray-600 text-xs font-mono font-bold">
                  {currentIndex + 1}/{level.sequence.length}
                </div>
                <div className="absolute top-2 right-3 bg-gray-900/50 px-2 py-0.5 rounded text-xs text-gray-500 border border-gray-700 font-mono">
                  Tu matricula: <strong className="text-green-400 tracking-wider">{matriculaJugador.replace(' ', '')}</strong>
                </div>

                {currentItem ? (
                  <>
                    <div className="text-center text-white text-sm font-bold tracking-widest uppercase mb-4 pb-3 border-b border-gray-700 mt-4">
                      LINEAS DE INSPECCION
                    </div>
                    <div className="grid grid-cols-[2fr_1fr] gap-4 mb-2 px-3">
                      <div className="text-gray-400 text-sm font-bold tracking-wider text-center pb-1 border-b border-gray-700">MATRICULA</div>
                      <div className="text-gray-400 text-sm font-bold tracking-wider text-center pb-1 border-b border-gray-700">LINEA</div>
                    </div>
                    <div className="grid grid-cols-[2fr_1fr] gap-4 px-3 py-3 bg-gray-900/50 rounded">
                      <div
                        className={`font-mono text-4xl font-bold tracking-[0.3em] ${ledColors.plate} animate-pulse`}
                        style={{ textShadow: '0 0 20px currentColor', filter: 'blur(0.3px)', fontFamily: 'Courier New, Consolas, monospace' }}
                      >
                        {currentItem.matricula.replace(' ', '')}
                      </div>
                      <div
                        className={`font-mono text-6xl font-bold ${ledColors.street} animate-pulse`}
                        style={{ textShadow: '0 0 25px currentColor', filter: 'blur(0.3px)', fontFamily: 'Courier New, Consolas, monospace' }}
                      >
                        {currentItem.calle}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-xl text-gray-600 italic text-center mt-16">Preparando...</div>
                )}
              </div>

              {/* Buttons */}
              <div className="grid grid-cols-2 gap-3">
                {(['A', 'B', 'C', 'D'] as const).map((letra, i) => (
                  <button
                    key={letra}
                    onClick={() => handleResponse(letra)}
                    className={`py-6 text-5xl font-bold border-4 border-gray-700 rounded-xl cursor-pointer transition-all shadow-lg text-white hover:scale-105 active:scale-95 bg-gradient-to-br ${
                      i === 0 ? 'from-red-600 to-red-800' :
                      i === 1 ? 'from-blue-600 to-blue-800' :
                      i === 2 ? 'from-green-600 to-green-800' :
                               'from-orange-600 to-orange-800'
                    }`}
                  >
                    {letra}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Results Screen */}
        {gameState === 'results' && results && (
          <div className="w-full max-w-lg bg-white/10 backdrop-blur-sm rounded-2xl p-5">
            <h2 className="text-2xl font-bold text-white mb-4 text-center">Resultados</h2>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-black/30 p-3 rounded-lg border-l-4 border-blue-400">
                <div className="text-xs text-blue-200 mb-1">Tiempo medio</div>
                <div className={`text-2xl font-bold ${results.tiempoMedio <= (level?.config?.tiempo_medio_maximo || 9999) ? 'text-green-400' : 'text-red-400'}`}>
                  {results.tiempoMedio} ms
                </div>
              </div>
              <div className="bg-black/30 p-3 rounded-lg border-l-4 border-blue-400">
                <div className="text-xs text-blue-200 mb-1">Aciertos</div>
                <div className={`text-2xl font-bold ${results.porcentajeAciertos >= (level?.config?.porcentaje_aciertos_minimo || 0) ? 'text-green-400' : 'text-red-400'}`}>
                  {results.porcentajeAciertos}%
                </div>
              </div>
              <div className="bg-black/30 p-3 rounded-lg border-l-4 border-blue-400">
                <div className="text-xs text-blue-200 mb-1">Objetivos</div>
                <div className="text-2xl font-bold text-white">{results.respondidos}/{results.objetivos}</div>
              </div>
              <div className="bg-black/30 p-3 rounded-lg border-l-4 border-blue-400">
                <div className="text-xs text-blue-200 mb-1">Errores</div>
                <div className="text-2xl font-bold text-white">{results.omisiones + results.confusiones}</div>
              </div>
            </div>

            <div className={`text-center py-4 rounded-xl mb-4 text-lg font-bold ${
              results.superado
                ? 'bg-green-600/30 text-green-300 border border-green-500'
                : 'bg-red-600/30 text-red-300 border border-red-500'
            }`}>
              {results.superado ? 'NIVEL SUPERADO' : 'Nivel no superado. Sigue practicando'}
            </div>

            <div className="flex gap-3 justify-center flex-wrap">
              <button
                onClick={resetGame}
                className="px-5 py-2 bg-yellow-500 text-gray-900 font-bold rounded-lg hover:bg-yellow-600 transition-all text-sm"
              >
                Reintentar
              </button>
              {results.superado && dificultadOrden < 5 && (
                <button
                  onClick={() => navigate(`/juegos/itv-express/${dificultadOrden + 1}`)}
                  className="px-5 py-2 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 transition-all text-sm"
                >
                  Siguiente nivel
                </button>
              )}
              <button
                onClick={() => navigate('/juegos')}
                className="px-5 py-2 bg-white/20 text-white font-bold rounded-lg hover:bg-white/30 transition-all text-sm"
              >
                Menu
              </button>
            </div>
          </div>
        )}
      </div>

      <VictoryModal
        isOpen={showVictory}
        onClose={() => {
          setShowVictory(false);
          resetGame();
        }}
        stats={{
          tiempo: Math.floor((Date.now() - gameStartTime) / 1000),
          movimientos: results?.porcentajeAciertos || 0,
        }}
      />
    </div>
  );
}
